import { useState, useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import FadeInSection from "@/components/FadeInSection";
import ArticleTagBadge from "@/components/ArticleTagBadge";
import MarketMoverBadge from "@/components/MarketMoverBadge";
import { getCategoryColor } from "@/lib/categoryColors";
import { getArticleUrl } from "@/lib/articleUrls";
import { getAuthor } from "@/data/journalists";
import { useVisitedArticles } from "@/hooks/useVisitedArticles";
import type { Article } from "@/components/NewsSection";
import { useMarketMovers } from "@/hooks/useMarketMovers";
import { SectionLabel } from "./SectionLabel";
import { timeAgo } from "./timeAgo";
import SanityImage from "@/components/SanityImage";

export const LatestNewsSection = ({ articles, isLoading = false }: { articles: Article[]; isLoading?: boolean }) => {
  const { isVisited } = useVisitedArticles();
  const { data: movers } = useMarketMovers();
  const items = articles.slice(0, 20);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const totalDots = Math.min(items.length, 10);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollLeft = el.scrollLeft;
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll > 0) {
            const ratio = scrollLeft / maxScroll;
            setActiveIndex(Math.round(ratio * (totalDots - 1)));
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [totalDots]);

  return (
    <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-10 min-h-[400px] md:min-h-[600px]">
      <FadeInSection delay={0.1}>
        <SectionLabel label="Latest News" href="/news" />
        {isLoading && items.length === 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-2 animate-pulse">
                <Skeleton className="aspect-[4/3] w-full rounded-md" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <>
        <div className="relative lg:static">
          <div
            ref={scrollRef}
            className="flex gap-2.5 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0 lg:gap-x-6 lg:gap-y-10"
          >
            {items.map((article) => {
              const colors = getCategoryColor(article.category);
              const author = getAuthor(article.slug, article.author);
              return (
                <Link
                  key={article.id}
                  to={getArticleUrl(article)}
                  className="group snap-start shrink-0 w-[40vw] sm:w-[200px] lg:w-auto flex flex-col h-full"
                >
                  <div className="aspect-[4/3] rounded-md bg-secondary overflow-hidden mb-2 relative shrink-0">
                    <SanityImage
                      src={article.image}
                      alt={article.title}
                      lqip={article.lqip}
                      className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100"
                      wrapperClassName="w-full h-full"
                      loading="lazy"
                      sizes="(max-width:768px) 50vw, 240px"
                      width={480}
                      height={360}
                    />
                    {article.tag && (
                      <div className="absolute top-1.5 left-1.5">
                        <ArticleTagBadge tag={article.tag} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      className="text-[10px] font-semibold tracking-wider uppercase"
                      style={{ color: colors.hex }}
                    >
                      {article.category}
                    </span>
                    {movers?.get(article.slug) && (
                      <MarketMoverBadge delta={movers.get(article.slug)!.delta_points} />
                    )}
                  </div>
                  <h3
                    className={`text-sm font-bold leading-snug mt-1 line-clamp-3 hover-highlight transition-colors shrink-0 ${isVisited(article.slug) ? "text-muted-foreground" : "text-foreground"}`}
                  >
                    {article.title}
                  </h3>
                  <p className="text-[11px] text-muted-foreground mt-auto pt-1.5 line-clamp-1 shrink-0">
                    {author ? `${author.name} · ${timeAgo(article.date)}` : timeAgo(article.date)}
                  </p>
                </Link>
              );
            })}
          </div>
          <div className="absolute right-0 top-0 bottom-3 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none lg:hidden" />
        </div>
        <div className="flex justify-center gap-1.5 pt-2 lg:hidden">
          {Array.from({ length: totalDots }).map((_, i) => (
            <span
              key={i}
              className={`block rounded-full transition-all duration-200 ${
                i === activeIndex ? "w-1.5 h-1.5 bg-foreground/70" : "w-1 h-1 bg-foreground/20"
              }`}
            />
          ))}
        </div>
          </>
        )}
      </FadeInSection>
    </div>
  );
};
